import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Download, ChevronRight } from "lucide-react";
import { useDeals, useCreateDeal, usePatchDeal, useDealReport, DEAL_STAGES, STAGE_LABEL, type Deal, type DealStage } from "@/hooks/useDeals.js";
import { useContacts } from "@/hooks/useContacts.js";
import { useAuth } from "@/auth/useAuth.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Badge } from "@/components/ui/badge.js";
import { env } from "@/env.js";

// Pratiche: pipeline incarico → proposta → compromesso → rogito.
// La provvigione è calcolata dall'API (importo fisso vince su %), qui si mostra.

const eur = (n: number) => "€ " + n.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function Deals(): JSX.Element {
  const { data, isLoading } = useDeals();
  const { data: report } = useDealReport();
  const [creating, setCreating] = useState(false);
  const patch = usePatchDeal();
  const { session } = useAuth();
  const items = data?.items ?? [];

  const byStage = useMemo(() => {
    const m = new Map<DealStage, Deal[]>();
    for (const s of DEAL_STAGES) m.set(s, []);
    for (const d of items) m.get(d.stage)?.push(d);
    return m;
  }, [items]);

  async function advance(deal: Deal) {
    const idx = DEAL_STAGES.indexOf(deal.stage);
    const next = DEAL_STAGES[idx + 1];
    if (!next || next === "persa") return;
    try {
      await patch.mutateAsync({ id: deal.id, stage: next });
      toast.success(`${deal.title} → ${STAGE_LABEL[next]}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }
  async function markLost(deal: Deal) {
    try {
      await patch.mutateAsync({ id: deal.id, stage: "persa" });
      toast.success(`${deal.title} segnata persa`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  async function downloadCsv() {
    // fetch con Bearer → blob → download (l'endpoint CSV è autenticato)
    const res = await fetch(`${env.VITE_API_BASE_URL}/api/v1/admin/deals/report?format=csv`, {
      headers: { Authorization: `Bearer ${session!.access_token}` },
    });
    if (!res.ok) { toast.error("Export non riuscito"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "report-provvigioni.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-display text-ink">Pratiche</h1>
          <p className="text-body text-ink-soft">{items.length} pratiche · incarico → rogito, con provvigioni</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => void downloadCsv()}><Download className="size-4" /> Report CSV</Button>
          <Button onClick={() => setCreating(true)}><Plus className="size-4" /> Nuova pratica</Button>
        </div>
      </header>

      {report ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Kpi label="Pratiche aperte" value={String(report.totals.deals - (report.by_stage["rogito"] ?? 0) - (report.by_stage["persa"] ?? 0))} />
          <Kpi label="Rogiti" value={String(report.totals.rogiti)} />
          <Kpi label="Provvigioni maturate" value={eur(report.totals.commissioni_maturate)} />
          <Kpi label="In pipeline" value={eur(report.totals.commissioni_pipeline)} />
        </div>
      ) : null}

      {creating ? <DealForm onClose={() => setCreating(false)} /> : null}

      {/* pipeline a colonne */}
      <div className="grid gap-3 lg:grid-cols-5">
        {DEAL_STAGES.map((stage) => {
          const list = byStage.get(stage) ?? [];
          return (
            <div key={stage} className="rounded border border-rule bg-surface">
              <div className="flex items-center justify-between border-b border-rule px-3 py-2">
                <span className="text-body-strong text-ink">{STAGE_LABEL[stage]}</span>
                <Badge>{list.length}</Badge>
              </div>
              <div className="space-y-2 p-2">
                {list.map((d) => (
                  <div key={d.id} className="space-y-1.5 rounded border border-rule-soft bg-bg p-3">
                    <p className="text-body-strong leading-tight text-ink">{d.title}</p>
                    <p className="font-mono text-meta text-ink-soft">
                      {d.price != null ? eur(d.price) : "prezzo n/d"}
                      {d.commissions.total > 0 ? <> · provv. <b className="text-ink">{eur(d.commissions.total)}</b></> : null}
                    </p>
                    <div className="flex items-center gap-2 pt-1">
                      {stage !== "rogito" && stage !== "persa" ? (
                        <>
                          <button onClick={() => void advance(d)} className="inline-flex items-center gap-0.5 text-meta text-accent hover:underline">
                            {STAGE_LABEL[DEAL_STAGES[DEAL_STAGES.indexOf(stage) + 1] as DealStage]} <ChevronRight className="size-3" />
                          </button>
                          <button onClick={() => void markLost(d)} className="text-meta text-ink-mute hover:text-danger">persa</button>
                        </>
                      ) : null}
                      <Link to={`/deals/${d.id}/proforma`} className="ml-auto inline-flex items-center gap-1 text-meta text-ink-soft hover:text-accent">
                        <FileText className="size-3" /> pro-forma
                      </Link>
                    </div>
                  </div>
                ))}
                {list.length === 0 ? <p className="px-2 py-4 text-center text-meta text-ink-mute">—</p> : null}
              </div>
            </div>
          );
        })}
      </div>

      {report && report.agents.length ? (
        <div className="overflow-x-auto rounded border border-rule bg-surface">
          <table className="w-full border-collapse text-body">
            <thead>
              <tr className="border-b border-rule text-left">
                <th className="p-3 text-meta uppercase text-ink-mute">Agente</th>
                <th className="p-3 text-meta uppercase text-ink-mute">Pratiche</th>
                <th className="p-3 text-meta uppercase text-ink-mute">Rogiti</th>
                <th className="p-3 text-right text-meta uppercase text-ink-mute">Maturate</th>
                <th className="p-3 text-right text-meta uppercase text-ink-mute">Pipeline</th>
              </tr>
            </thead>
            <tbody>
              {report.agents.map((a) => (
                <tr key={a.agent} className="border-b border-rule-soft last:border-b-0">
                  <td className="p-3 text-ink">{a.agent}</td>
                  <td className="p-3 font-mono">{a.deals}</td>
                  <td className="p-3 font-mono">{a.rogiti}</td>
                  <td className="p-3 text-right font-mono font-semibold text-ink">{eur(a.commissioni_maturate)}</td>
                  <td className="p-3 text-right font-mono text-ink-soft">{eur(a.commissioni_pipeline)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {!isLoading && items.length === 0 ? (
        <p className="py-8 text-center text-ink-mute">Nessuna pratica. Parti da "Nuova pratica": bastano titolo e fase.</p>
      ) : null}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded border border-rule bg-surface p-4">
      <p className="font-mono text-[20px] font-semibold text-ink">{value}</p>
      <p className="text-meta uppercase tracking-wide text-ink-mute">{label}</p>
    </div>
  );
}

function DealForm({ onClose }: { onClose: () => void }): JSX.Element {
  const create = useCreateDeal();
  const { data: contactsData } = useContacts();
  const contacts = contactsData?.items ?? [];
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [sellerPct, setSellerPct] = useState("3");
  const [buyerPct, setBuyerPct] = useState("3");

  async function save() {
    try {
      await create.mutateAsync({
        title: title.trim(),
        price: price ? Number(price) : null,
        seller_contact_id: sellerId || null,
        buyer_contact_id: buyerId || null,
        commission_seller_pct: sellerPct ? Number(sellerPct) : null,
        commission_buyer_pct: buyerPct ? Number(buyerPct) : null,
      });
      toast.success("Pratica creata");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore creazione");
    }
  }

  const selectCls = "h-10 rounded border border-rule bg-bg px-3 text-body text-ink";
  return (
    <div className="space-y-4 rounded border border-accent/40 bg-surface p-5">
      <h2 className="text-body-strong text-ink">Nuova pratica</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input placeholder='Titolo * (es. "Vendita villa Peschiera · Rossi")' value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input placeholder="Prezzo €" type="number" value={price} onChange={(e) => setPrice(e.target.value)} />
        <select className={selectCls} value={sellerId} onChange={(e) => setSellerId(e.target.value)}>
          <option value="">Venditore (contatto)…</option>
          {contacts.map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
        </select>
        <select className={selectCls} value={buyerId} onChange={(e) => setBuyerId(e.target.value)}>
          <option value="">Acquirente (contatto)…</option>
          {contacts.map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
        </select>
        <Input placeholder="Provvigione venditore %" type="number" value={sellerPct} onChange={(e) => setSellerPct(e.target.value)} />
        <Input placeholder="Provvigione acquirente %" type="number" value={buyerPct} onChange={(e) => setBuyerPct(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => void save()} disabled={create.isPending || title.trim().length < 2}>{create.isPending ? "Creo…" : "Crea pratica"}</Button>
        <Button variant="ghost" onClick={onClose}>Annulla</Button>
      </div>
    </div>
  );
}
